import React, { useState, useEffect } from 'react';
import { settingsApi, GlobalSetting } from '../../services/api';

export function GlobalSettings(): JSX.Element {
  const [settings, setSettings] = useState<GlobalSetting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [showAddForm, setShowAddForm] = useState(false);

  // New setting form
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [newType, setNewType] = useState<'boolean' | 'number' | 'string' | 'option'>('string');
  const [newDescription, setNewDescription] = useState('');
  const [newOptions, setNewOptions] = useState<string[]>([]);
  const [newOptionInput, setNewOptionInput] = useState('');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const response = await settingsApi.getGlobalWithMeta();
      setSettings(response.settings || []);
    } catch (err) {
      setError('Fehler beim Laden der Einstellungen');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const startEdit = (setting: GlobalSetting) => {
    setEditingKey(setting.key);
    setEditValue(String(setting.value));
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditValue('');
  };

  const saveEdit = async (setting: GlobalSetting) => {
    try {
      let parsedValue: boolean | number | string = editValue;

      if (setting.type === 'boolean') {
        parsedValue = editValue === 'true';
      } else if (setting.type === 'number') {
        parsedValue = Number(editValue);
        if (isNaN(parsedValue)) {
          setError('Ungültiger Zahlenwert');
          return;
        }
      }
      // Für 'option' Typ bleibt parsedValue als String

      await settingsApi.upsertSetting({
        key: setting.key,
        value: parsedValue,
        type: setting.type,
        description: setting.description,
        ...(setting.type === 'option' && setting.options ? { options: setting.options } : {}),
      });

      setEditingKey(null);
      loadSettings();
    } catch (err: any) {
      setError(err.message || 'Fehler beim Speichern');
    }
  };

  const handleAddSetting = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setIsSubmitting(true);

    try {
      let parsedValue: boolean | number | string = newValue;

      if (newType === 'boolean') {
        parsedValue = newValue === 'true';
      } else if (newType === 'number') {
        parsedValue = Number(newValue);
        if (isNaN(parsedValue)) {
          setFormError('Ungültiger Zahlenwert');
          setIsSubmitting(false);
          return;
        }
      } else if (newType === 'option') {
        if (newOptions.length === 0) {
          setFormError('Mindestens eine Option muss angegeben werden');
          setIsSubmitting(false);
          return;
        }
        if (!newOptions.includes(newValue)) {
          setFormError('Der gewählte Wert muss eine der Optionen sein');
          setIsSubmitting(false);
          return;
        }
      }

      await settingsApi.upsertSetting({
        key: newKey,
        value: parsedValue,
        type: newType,
        description: newDescription,
        ...(newType === 'option' ? { options: newOptions } : {}),
      });

      setShowAddForm(false);
      setNewKey('');
      setNewValue('');
      setNewType('string');
      setNewDescription('');
      setNewOptions([]);
      setNewOptionInput('');
      loadSettings();
    } catch (err: any) {
      setFormError(err.message || 'Fehler beim Hinzufügen');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (key: string) => {
    if (!window.confirm(`Einstellung "${key}" wirklich löschen?`)) return;

    try {
      await settingsApi.deleteSetting(key);
      loadSettings();
    } catch (err: any) {
      setError(err.message || 'Fehler beim Löschen');
    }
  };

  const renderValueInput = (setting: GlobalSetting) => {
    if (setting.type === 'boolean') {
      return (
        <select
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          className="inline-select"
        >
          <option value="true">Ja</option>
          <option value="false">Nein</option>
        </select>
      );
    }

    if (setting.type === 'option' && setting.options) {
      return (
        <select
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          className="inline-select"
        >
          {setting.options.map(opt => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        type={setting.type === 'number' ? 'number' : 'text'}
        value={editValue}
        onChange={e => setEditValue(e.target.value)}
        className="inline-input"
      />
    );
  };

  const formatValue = (setting: GlobalSetting) => {
    if (setting.type === 'boolean') {
      return setting.value ? 'Ja' : 'Nein';
    }
    if (setting.type === 'option') {
      // Zeige den ausgewählten Wert mit einem Hinweis auf die verfügbaren Optionen
      return String(setting.value);
    }
    return String(setting.value);
  };

  const addOption = () => {
    const trimmed = newOptionInput.trim();
    if (trimmed && !newOptions.includes(trimmed)) {
      setNewOptions([...newOptions, trimmed]);
      setNewOptionInput('');
    }
  };

  const removeOption = (opt: string) => {
    setNewOptions(newOptions.filter(o => o !== opt));
    if (newValue === opt) {
      setNewValue('');
    }
  };

  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Laden...</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>Globale Einstellungen</h1>
        {!showAddForm && (
          <button className="btn btn-primary" onClick={() => setShowAddForm(true)}>
            Einstellung hinzufügen
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {showAddForm && (
        <div className="form-card">
          <h2>Neue Einstellung</h2>
          <form onSubmit={handleAddSetting}>
            {formError && <div className="form-error">{formError}</div>}

            <div className="form-group">
              <label htmlFor="newKey">Schlüssel</label>
              <input
                type="text"
                id="newKey"
                value={newKey}
                onChange={e => setNewKey(e.target.value)}
                placeholder="z.B. maxSessionDuration"
                required
                pattern="[a-zA-Z][a-zA-Z0-9_]*"
                title="Muss mit Buchstabe beginnen, nur Buchstaben, Zahlen und Unterstriche"
              />
            </div>

            <div className="form-group">
              <label htmlFor="newType">Typ</label>
              <select
                id="newType"
                value={newType}
                onChange={e => {
                  setNewType(e.target.value as any);
                  setNewValue('');
                  setNewOptions([]);
                }}
              >
                <option value="string">Text</option>
                <option value="number">Zahl</option>
                <option value="boolean">Ja/Nein</option>
                <option value="option">Dropdown (Auswahl)</option>
              </select>
            </div>

            {newType === 'option' && (
              <div className="form-group">
                <label>Optionen definieren</label>
                <div className="options-editor">
                  <div className="options-input-row">
                    <input
                      type="text"
                      value={newOptionInput}
                      onChange={e => setNewOptionInput(e.target.value)}
                      placeholder="Option eingeben"
                      onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addOption())}
                    />
                    <button type="button" className="btn btn-small btn-secondary" onClick={addOption}>
                      Hinzufügen
                    </button>
                  </div>
                  {newOptions.length > 0 && (
                    <div className="options-list">
                      {newOptions.map(opt => (
                        <span key={opt} className="option-tag">
                          {opt}
                          <button type="button" className="option-remove" onClick={() => removeOption(opt)}>
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="newValue">Wert</label>
              {newType === 'boolean' ? (
                <select
                  id="newValue"
                  value={newValue}
                  onChange={e => setNewValue(e.target.value)}
                >
                  <option value="">-- Bitte wählen --</option>
                  <option value="true">Ja</option>
                  <option value="false">Nein</option>
                </select>
              ) : newType === 'option' ? (
                <select
                  id="newValue"
                  value={newValue}
                  onChange={e => setNewValue(e.target.value)}
                  disabled={newOptions.length === 0}
                >
                  <option value="">-- Bitte wählen --</option>
                  {newOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : (
                <input
                  type={newType === 'number' ? 'number' : 'text'}
                  id="newValue"
                  value={newValue}
                  onChange={e => setNewValue(e.target.value)}
                  required
                />
              )}
            </div>

            <div className="form-group">
              <label htmlFor="newDescription">Beschreibung</label>
              <input
                type="text"
                id="newDescription"
                value={newDescription}
                onChange={e => setNewDescription(e.target.value)}
                placeholder="Kurze Beschreibung der Einstellung"
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Speichern...' : 'Speichern'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowAddForm(false)}>
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="settings-list">
        {settings.length === 0 ? (
          <p className="no-data">Keine Einstellungen vorhanden</p>
        ) : (
          settings.map(setting => (
            <div key={setting.key} className="setting-item">
              <div className="setting-header">
                <code className="setting-key">{setting.key}</code>
                <span className={`type-badge ${setting.type}`}>{setting.type}</span>
              </div>

              {setting.description && (
                <p className="setting-description">{setting.description}</p>
              )}

              {setting.type === 'option' && setting.options && setting.options.length > 0 && (
                <div className="setting-options">
                  <span className="options-label">Verfügbare Optionen:</span>
                  {setting.options.map(opt => (
                    <span
                      key={opt}
                      className={`option-badge ${String(setting.value) === opt ? 'selected' : ''}`}
                    >
                      {opt}
                    </span>
                  ))}
                </div>
              )}

              <div className="setting-value-row">
                {editingKey === setting.key ? (
                  <>
                    {renderValueInput(setting)}
                    <button className="btn btn-small btn-success" onClick={() => saveEdit(setting)}>
                      Speichern
                    </button>
                    <button className="btn btn-small" onClick={cancelEdit}>
                      Abbrechen
                    </button>
                  </>
                ) : (
                  <>
                    <span className="setting-value">{formatValue(setting)}</span>
                    <button className="btn btn-small" onClick={() => startEdit(setting)}>
                      Bearbeiten
                    </button>
                    <button className="btn btn-small btn-danger" onClick={() => handleDelete(setting.key)}>
                      Löschen
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default GlobalSettings;
